/**
 * Media API - Upload URL Generation
 *
 * Single and batch upload URL generation endpoints, plus upload utilities.
 */

import { z } from 'zod'
import { UploadContextSchema, MediaVisibilitySchema } from './common'

// ============================================================================
// Constants
// ============================================================================

/**
 * Maximum number of files allowed in a single batch upload — albums,
 * collections, and the post composer.
 *
 * Raised 10 → 100 (2026-08-10, SCH-914/GRO-102) because teachers post a whole
 * event's photos at once and 10 forced them into ten separate posts.
 *
 * What makes a large batch safe is the mobile engine's BOUNDED CONCURRENCY,
 * not the batch size: at most `MAX_CONCURRENT_UPLOADS` photos are in flight,
 * videos run one at a time and last, and progress is ambient behind the
 * floating chip. Peak memory is set by the pool, so it does not grow with the
 * ceiling. (Web has no such pool — see SCH-919.)
 *
 * Backend `MAX_BATCH_UPLOAD_FILES` (presign) and the `/v1/media/batch`
 * record-create cap move with it — keep the three numbers equal.
 *
 * Chat is deliberately NOT covered — see `MAX_CHAT_BATCH_FILES`.
 */
export const MAX_BATCH_FILES = 100

/**
 * Per-message attachment ceiling for chat, held at the historical 10.
 *
 * Chat attachments are a different act from publishing an album: they render
 * inline in a message list, and the send path has no ambient progress surface
 * to absorb a 100-file batch. Split from `MAX_BATCH_FILES` when that constant
 * was raised, so chat did not silently inherit 100.
 */
export const MAX_CHAT_BATCH_FILES = 10

/** Allowed image MIME types */
export const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif'
] as const

/** Allowed video MIME types */
export const ALLOWED_VIDEO_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/x-msvideo',
  'video/webm',
  'video/x-m4v'
] as const

/**
 * Allowed document MIME types.
 *
 * Mirrors the backend's canonical `MEDIA_TYPES.DOCUMENT` list
 * (`apps/backend/src/services/media-library/media/shared/constants.ts`) — the
 * contract chat already enforces, reused verbatim for post attachments rather
 * than authored fresh (post-document-attachments plan, KD2).
 */
export const ALLOWED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-powerpoint', // .ppt
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  'text/plain', // .txt
  'text/csv', // .csv
  'application/zip', // .zip
  'application/x-zip-compressed' // .zip (alternative)
] as const

/**
 * Document MIME types a POST may carry — a deliberate SUBSET of
 * {@link ALLOWED_DOCUMENT_TYPES}, not a replacement for it (W4).
 *
 * The product scoped post attachments to exactly four file families: PDF, Word,
 * Excel, PowerPoint. The wide list above stays as-is because it is the contract
 * CHAT enforces, and chat legitimately accepts txt / csv / zip — narrowing the
 * shared list would silently regress chat attachments. So the scope is expressed
 * as a second, narrower list applied only on the post path (the composer's
 * validation gate), while transport routing and the backend allowlist keep
 * reading the wide one.
 *
 * The `satisfies` clause is the anti-drift guard: adding a mime here that is not
 * in `ALLOWED_DOCUMENT_TYPES` — i.e. something the backend would reject anyway —
 * fails to compile.
 */
export const POST_DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-powerpoint', // .ppt
  'application/vnd.openxmlformats-officedocument.presentationml.presentation' // .pptx
] as const satisfies readonly (typeof ALLOWED_DOCUMENT_TYPES)[number][]

/**
 * Author-facing name of each supported post-document family, for the rejection
 * copy. Kept beside the list so the two cannot describe different things.
 */
export const POST_DOCUMENT_FAMILIES_LABEL = 'PDF, Word, Excel or PowerPoint'

/**
 * Allowed audio MIME types.
 *
 * Verbatim mirror of the backend's audio allowlist, which exists in two
 * identical copies: `ALLOWED_AUDIO_TYPES` in
 * `apps/backend/src/interfaces/aws/s3/index.ts` (what the presign route
 * actually enforces) and `ALLOWED_MEDIA_TYPES.AUDIO` in
 * `apps/backend/src/services/media-library/media/shared/constants.ts`.
 *
 * Drift here is not cosmetic: `getMediaTypeFromMimeType` derives `PHOTO` for a
 * mime it does not recognise, so an audio type this list carries and the
 * backend does not would be filed as a photo and handed a Cloudinary image
 * transform over an mp3. `s3-presigned-accept-parity.test.ts` pins the three
 * lists together for exactly that reason.
 */
export const ALLOWED_AUDIO_TYPES = [
  'audio/mpeg', // .mp3
  'audio/mp4', // .m4a
  'audio/x-m4a', // .m4a (alternative)
  'audio/mp3', // .mp3 (alternative)
  'audio/wav', // .wav
  'audio/x-wav', // .wav (alternative)
  'audio/ogg', // .ogg
  'audio/aac', // .aac
  'audio/x-aac', // .aac (alternative)
  'audio/webm', // .webm audio
  'audio/flac', // .flac
  'audio/x-flac' // .flac (alternative)
] as const

/**
 * Audio MIME types a POST may carry — a deliberate SUBSET of
 * {@link ALLOWED_AUDIO_TYPES}, mirroring the document wide/narrow split above.
 *
 * The wide list stays as-is because it is the contract CHAT's voice notes and
 * the presign route enforce; narrowing it would regress those. The post scope is
 * the four families an author is offered (MP3, M4A, WAV, AAC), spelled with EVERY
 * alternative mime the wide list carries for those four. A family is in scope or
 * it is not: dropping one spelling would reject a file the author picked from the
 * offered family, with a message naming that same family as allowed.
 *
 * The `satisfies` clause is the anti-drift guard: a mime here that the backend
 * would reject anyway fails to compile.
 */
export const POST_AUDIO_TYPES = [
  'audio/mpeg', // .mp3
  'audio/mp3', // .mp3 (alternative — some Android pickers report this)
  'audio/mp4', // .m4a
  'audio/x-m4a', // .m4a (alternative)
  'audio/wav', // .wav
  'audio/x-wav', // .wav (alternative)
  'audio/aac', // .aac
  'audio/x-aac' // .aac (alternative)
] as const satisfies readonly (typeof ALLOWED_AUDIO_TYPES)[number][]

/**
 * Author-facing name of each supported post-audio family, for the rejection
 * copy. Kept beside the list so the two cannot describe different things.
 */
export const POST_AUDIO_FAMILIES_LABEL = 'MP3, M4A, WAV or AAC'

/**
 * Per-file audio size ceiling: 100MB — mirrors the backend's `MAX_AUDIO_SIZE`
 * (`apps/backend/src/interfaces/aws/s3/index.ts`). This is the only audio limit
 * the server can enforce on its own: nothing on the server reads an audio
 * file's length, so the duration ceiling below is checked against a STATED
 * duration, not a measured one.
 */
export const MAX_AUDIO_UPLOAD_SIZE = 100 * 1024 * 1024

/**
 * Longest audio a post may carry, in seconds (10 minutes) — mirrors the
 * backend's `MEDIA_LIMITS.MAX_AUDIO_DURATION`.
 *
 * Best-effort by construction: the duration a create request carries is
 * MEASURED ON THE DEVICE after the pick and sent with the request, so a caller
 * talking to the API directly, or a file the device could not measure, can
 * exceed it. The server rejects a stated duration over this; the size ceiling
 * above is the hard backstop.
 */
export const MAX_AUDIO_DURATION_SECONDS = 600

/** Maximum file size: 100MB */
export const MAX_FILE_SIZE = 100 * 1024 * 1024

/**
 * Ceiling the S3 presigned upload path (`upload-url` / `batch-upload-url`)
 * actually enforces for an image — mirrors the backend's `MAX_IMAGE_SIZE`
 * (`apps/backend/src/interfaces/aws/s3/index.ts`).
 *
 * NOT the same as {@link MAX_FILE_SIZE}, which is the wider transport ceiling
 * covering audio/documents too. Picking an image between 50MB and 100MB used
 * to pass client validation and then fail server-side with a 400 (SCH-1009).
 */
export const MAX_IMAGE_UPLOAD_SIZE = 50 * 1024 * 1024

/**
 * Value for the `accept` attribute of any file input whose bytes go to the S3
 * presigned path. Derived from {@link ALLOWED_IMAGE_TYPES} rather than
 * hand-listed so a picker can never advertise a type the route rejects.
 *
 * Video is deliberately ABSENT: the S3 path rejects every video MIME
 * (see {@link rejectVideoMimeType}) because video must be transcoded by Mux.
 * A picker that wants video must drive the Mux direct-upload lane instead.
 */
export const S3_UPLOAD_ACCEPT_ATTRIBUTE = ALLOWED_IMAGE_TYPES.join(',')

/** Author-facing name of each image family the S3 presigned path accepts. */
export const S3_UPLOAD_IMAGE_FAMILIES_LABEL = 'JPEG, PNG, WebP, GIF, HEIC'

/** Per-file document size ceiling: 100MB — the ceiling chat already applies. */
export const MAX_DOCUMENT_SIZE = 100 * 1024 * 1024

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Upload progress tracking for batch uploads
 */
export const UploadProgressSchema = z.object({
  fileName: z.string(),
  progress: z.number().min(0).max(100),
  status: z.enum(['pending', 'uploading', 'creating', 'complete', 'error']),
  error: z.string().optional(),
  mediaId: z.string().optional()
})

export type UploadProgress = z.infer<typeof UploadProgressSchema>

/**
 * Options for media upload
 */
export interface UploadOptions {
  title?: string
  description?: string
  tags?: string[]
  visibility?: z.infer<typeof MediaVisibilitySchema>
  onProgress?: (progress: number) => void
}

/**
 * Response data from upload URL generation
 */
export const UploadUrlResponseSchema = z.object({
  uploadUrl: z.string(),
  mediaId: z.string(),
  s3Key: z.string(),
  expiresAt: z.string(),
  maxFileSize: z.number(),
  collectionId: z.string().optional()
})

export type UploadUrlResponse = z.infer<typeof UploadUrlResponseSchema>

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format bytes as human-readable size (e.g., "1.5 MB").
 *
 * Accepts an absent or unusable value and returns an EMPTY string for it,
 * rather than `NaN Bytes` or `undefined` — callers routinely hold a nullable
 * `fileSize` from a provider, and an empty string drops out of a
 * `.filter(Boolean).join(' · ')` metadata line on its own. This is the one
 * canonical byte formatter for values that cross the wire; do not add a
 * fourth local copy.
 */
export function formatBytes(bytes: string | number | null | undefined): string {
  if (bytes == null) return ''
  const numBytes = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes
  if (!Number.isFinite(numBytes) || numBytes < 0) return ''
  if (numBytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(numBytes) / Math.log(k))

  return `${parseFloat((numBytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

/**
 * Typed error code surfaced when a video MIME type is sent to an S3 presigned
 * upload path. Video MUST route through the Mux direct-upload endpoint
 * (`mux-upload-url`) so it is transcoded; the S3 presigned paths
 * (`upload-url` / `upload-urls`) reject it. Mirrors the inverse of
 * `MuxUploadUrlRequestSchema`'s refine, which rejects non-video on the Mux path.
 */
export const VIDEO_REQUIRES_MUX_CODE = 'VIDEO_REQUIRES_MUX' as const

/**
 * Human-readable message for {@link VIDEO_REQUIRES_MUX_CODE}.
 */
export const VIDEO_REQUIRES_MUX_MESSAGE =
  'Video uploads must use the Mux upload endpoint (mux-upload-url), not the S3 presigned path'

/**
 * The same fact stated for a person rather than an API caller. Surfaced when a
 * video is picked in a UI whose upload goes to the S3 presigned path.
 */
export const VIDEO_REQUIRES_MUX_UI_MESSAGE = 'Videos cannot be uploaded here — use the "Upload Video" button instead'

/**
 * Refine predicate that REJECTS video MIME types. Use on the S3 presigned
 * upload schemas (single + batch) so video can never bypass Mux transcoding.
 * Returns `true` for any non-video type (the value passes), `false` for video.
 */
export function rejectVideoMimeType(mimeType: string): boolean {
  return !(ALLOWED_VIDEO_TYPES as readonly string[]).includes(mimeType)
}

/**
 * Check if a MIME type is an image type
 */
export function isImageType(mimeType: string): boolean {
  return (ALLOWED_IMAGE_TYPES as readonly string[]).includes(mimeType)
}

/**
 * Check if a MIME type is a video type
 */
export function isVideoType(mimeType: string): boolean {
  return (ALLOWED_VIDEO_TYPES as readonly string[]).includes(mimeType)
}

/**
 * Check if a MIME type is a document type
 */
export function isDocumentType(mimeType: string): boolean {
  return (ALLOWED_DOCUMENT_TYPES as readonly string[]).includes(mimeType)
}

/**
 * Check if a MIME type is a document a POST may carry.
 *
 * Strictly narrower than {@link isDocumentType}: a txt / csv / zip is a document
 * (chat may upload it) but is NOT post-attachable. Use this on the post path
 * only — see {@link POST_DOCUMENT_TYPES}.
 */
export function isPostDocumentType(mimeType: string): boolean {
  return (POST_DOCUMENT_TYPES as readonly string[]).includes(mimeType)
}

/**
 * Check if a MIME type is an audio type
 */
export function isAudioType(mimeType: string): boolean {
  return (ALLOWED_AUDIO_TYPES as readonly string[]).includes(mimeType)
}

/**
 * Check if a MIME type is audio a POST may carry.
 *
 * Strictly narrower than {@link isAudioType}: an ogg / flac / webm recording is
 * audio (chat may upload it) but is NOT post-attachable. Use this on the post
 * path only — see {@link POST_AUDIO_TYPES}.
 */
export function isPostAudioType(mimeType: string): boolean {
  return (POST_AUDIO_TYPES as readonly string[]).includes(mimeType)
}

/**
 * Validate a file picked for an S3 presigned upload (`upload-url` /
 * `batch-upload-url`) — every caller of this function is on that path.
 *
 * It is deliberately pinned to what that route ACCEPTS, not to the widest
 * thing the media library can hold:
 *
 * - Video is rejected here, with the message that names the lane that works.
 *   It used to be admitted, so a picked video was uploaded, presigned, and
 *   then 400'd by the route's own Zod refine — a guaranteed failure the UI
 *   invited (SCH-1009).
 * - Size is checked against {@link MAX_IMAGE_UPLOAD_SIZE} (50MB, what the
 *   server enforces for images) rather than {@link MAX_FILE_SIZE} (100MB), so
 *   a 70MB photo is refused at pick time instead of after the round trip.
 *
 * Type is checked BEFORE size so an oversized video still gets the Mux
 * message, which is the actionable one.
 */
export function validateFile(file: File): { valid: boolean; error?: string } {
  if (isVideoType(file.type)) {
    return {
      valid: false,
      error: VIDEO_REQUIRES_MUX_UI_MESSAGE
    }
  }

  if (!isImageType(file.type)) {
    return {
      valid: false,
      error: `File type ${file.type} is not supported`
    }
  }

  if (file.size > MAX_IMAGE_UPLOAD_SIZE) {
    return {
      valid: false,
      error: `File size exceeds ${formatBytes(MAX_IMAGE_UPLOAD_SIZE)} limit`
    }
  }

  return { valid: true }
}

// ============================================================================
// POST /v1/media/upload-url
// ============================================================================

export const MediaGenerateUploadUrlRequestSchema = z.object({
  fileName: z.string().min(1).max(500),
  contentType: z.string().min(1),
  fileSize: z.number().positive(),
  context: UploadContextSchema.optional()
})

export type MediaGenerateUploadUrlRequest = z.infer<typeof MediaGenerateUploadUrlRequestSchema>

export const MediaGenerateUploadUrlResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    uploadUrl: z.string(),
    mediaId: z.string(),
    s3Key: z.string(),
    expiresAt: z.string(),
    maxFileSize: z.number(),
    collectionId: z.string().optional()
  })
})

export type MediaGenerateUploadUrlResponse = z.infer<typeof MediaGenerateUploadUrlResponseSchema>

// ============================================================================
// POST /v1/media/mux-upload-url
// ============================================================================

/**
 * Request schema for the Mux direct-upload URL endpoint.
 *
 * The endpoint creates a Mux Direct Upload, persists a Media row in the
 * PROCESSING state with `muxAssetId` populated, and returns the Mux-issued
 * upload URL for the client to PUT bytes directly to.
 */
export const MuxUploadUrlRequestSchema = z.object({
  fileName: z.string().min(1).max(500),
  contentType: z
    .string()
    .min(1)
    .refine(val => (ALLOWED_VIDEO_TYPES as readonly string[]).includes(val), {
      message: 'Mux upload requires a supported video MIME type'
    }),
  fileSize: z.number().positive().optional(),
  context: UploadContextSchema.optional(),
  // Capture timestamp + GPS read from the picker asset's EXIF at pick time
  // (SCH-140). WRITE-ONLY: persisted on the Media row but NEVER serialized into
  // any client-facing read response (FERPA; future location filtering is
  // server-side).
  capturedAt: z.coerce.date().optional(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional()
})

export type MuxUploadUrlRequest = z.infer<typeof MuxUploadUrlRequestSchema>

export const MuxUploadUrlResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    mediaId: z.string(),
    uploadUrl: z.string(),
    muxAssetId: z.string(),
    collectionId: z.string().optional()
  })
})

export type MuxUploadUrlResponse = z.infer<typeof MuxUploadUrlResponseSchema>

// ============================================================================
// POST /v1/media/upload-urls (Batch)
// ============================================================================

export const BatchFileSchema = z.object({
  fileName: z.string().min(1).max(500),
  contentType: z.string().min(1),
  fileSize: z.number().positive()
})

export type BatchFile = z.infer<typeof BatchFileSchema>

export const GenerateBatchUploadUrlsRequestSchema = z.object({
  files: z.array(BatchFileSchema).min(1),
  context: UploadContextSchema.optional()
})

export type GenerateBatchUploadUrlsRequest = z.infer<typeof GenerateBatchUploadUrlsRequestSchema>

export const GenerateBatchUploadUrlsResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    files: z.array(
      z.object({
        uploadUrl: z.string(),
        mediaId: z.string(),
        s3Key: z.string(),
        expiresAt: z.string(),
        maxFileSize: z.number()
      })
    ),
    totalFiles: z.number(),
    collectionId: z.string().optional()
  })
})

export type GenerateBatchUploadUrlsResponse = z.infer<typeof GenerateBatchUploadUrlsResponseSchema>
